// Figma: "Community Dashboard" page (node 18034:1276), "Dashboard-Body" content area.
// Branches on params.campaign_sfid to show the campaign detail composition instead of the
// portfolio-level dashboard — mirrors LC2's TodayTab.js switching between PortfolioTab/
// CampaignTab (via the `openItem` reducer state there; done directly off route params
// here since DashboardPage already receives them). This drill-down takes top priority,
// same as LC2's URL-based drill-down which overrode whatever tab was selected.
//
// Next: if `params.community_sfid` is present, the Communities section renders regardless
// of which sidebar tab happens to be selected — mirroring LC2, where the URL (not the
// sidebar/tab state) decided whether CommunityTab or PortfolioTab rendered (see
// commons/components/HomeTabs/Today/TodayTab.js's `openItem === 'communities'` branch,
// driven by MainLayout's location-based `activeItem` dispatch, not by `openTab`). This is
// also what the sidebar's "Communities" destination shows when no community is selected
// yet (`params.community_sfid` unset) via the `openTab === 'communities'` branch below.
//
// Below that, branches on the sidebar's `openTab` (menu reducer, see Sidebar.js /
// app/store/reducers/menu.js): 'today' (or no tab selected yet, matching Sidebar's
// default) shows the existing Today dashboard; 'esg' shows the ESG section (see
// Esg/EsgSection.js, ported from LC2's EsgTab.js); 'communities' shows the Communities
// section (see Communities/CommunitySection.js, ported from LC2's CommunityTab.js);
// 'engagement' shows the Engagement section (see Engagement/EngagementSection.js, ported
// from LC2's EngagementTab.js); 'crm' shows the CRM section (see Crm/CrmSection.js, ported
// from LC2's CrmTab.js — this also carries LC2's Invoice component, see that file's header
// comment for why); 'stakesay' shows the StakeSay section (see
// StakeSay/StakeSaySection.js, ported from LC2's StakeSayTab.js); 'reports' shows the
// Reports section (see Reports/ReportsSection.js, ported from LC2's ReportsTable.js, gated
// the same way LC2's TodayTab.js gated it); 'invoices' shows the Invoices section (see
// Invoices/InvoicesSection.js — its own Figma page, node 18698:9037; LC2 never had a
// standalone Invoices tab, only the combined "CRM / Invoices" tab whose accordion-based
// Invoice component lives on unchanged at Crm/Invoice.js); 'performance' shows the
// Performance section (see
// Performance/PerformanceSection.js, ported from LC2's PerformanceTab.js — it re-branches
// internally on `openItem`, same as LC2, to show the account/community/campaign-level
// composition). `NOT_PORTED_LABELS` is currently empty — kept as scaffolding for the next
// tab that still needs porting.
import React from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {Box, Stack, Typography} from '@mui/material';

import StatCards from './StatCards';
import SectionNav from './SectionNav';
import ResidentActionFeedTable from './ResidentActionFeedTable';
import CampaignsTable from './CampaignsTable';
import AlertsSection from './AlertsSection';
import GlanceSection from './Glance/GlanceSection';
import CampaignDetailPage from './CampaignDetailPage';
import EsgSection from './Esg/EsgSection';
import CommunitySection from './Communities/CommunitySection';
import EngagementSection from './Engagement/EngagementSection';
import CrmSection from './Crm/CrmSection';
import StakeSaySection from './StakeSay/StakeSaySection';
import ReportsSection from './Reports/ReportsSection';
import InvoicesSection from './Invoices/InvoicesSection';
import PerformanceSection from './Performance/PerformanceSection';
import {getOpenTab} from '../../app/store/reducers/menu';

const NOT_PORTED_LABELS = {};

const TodayDashboard = () => (
    <Stack spacing={3}>
        <StatCards />
        <SectionNav />
        <Box id="resident-action-feed">
            <ResidentActionFeedTable />
        </Box>
        <Box id="at-a-glance">
            <GlanceSection />
        </Box>
        <Box id="campaigns">
            <CampaignsTable />
        </Box>
        <Box id="alerts">
            <AlertsSection />
        </Box>
    </Stack>
);

const NotPortedTab = ({label}) => (
    <Typography variant="body2" color="text.secondary">
        {label} hasn&apos;t been ported to the new UI yet.
    </Typography>
);

const DashboardPage = () => {
    const params = useParams();
    const openTab = useSelector(getOpenTab);

    if (params.campaign_sfid) {
        return <CampaignDetailPage />;
    }

    if (openTab === 'esg') {
        return <EsgSection />;
    }

    if (openTab === 'communities') {
        return <CommunitySection />;
    }

    if (openTab === 'engagement') {
        return <EngagementSection />;
    }

    if (openTab === 'crm') {
        return <CrmSection />;
    }

    if (openTab === 'stakesay') {
        return <StakeSaySection />;
    }

    if (openTab === 'reports') {
        return <ReportsSection />;
    }

    // Figma: "Invoices / All Communities / Expanded" and siblings (node 18698:9037) — the
    // sidebar's "Invoices" destination now has its own dedicated table page (see
    // Invoices/InvoicesSection.js), replacing the earlier placeholder that reused the CRM
    // tab's accordion-based Invoice component (Crm/Invoice.js, still used as-is inside the
    // CRM tab — see Crm/CrmSection.js's header comment for why that one stays).
    if (openTab === 'invoices') {
        return <InvoicesSection />;
    }

    // See Performance/PerformanceSection.js's header comment — ported from LC2's
    // PerformanceTab.js, switching on `openItem` (portfolio/communities/campaigns) the same
    // way LC2 did, independent of this `openTab` branch.
    if (openTab === 'performance') {
        return <PerformanceSection />;
    }

    // Falls through here only when no specific sidebar tab is active (openTab is 'today',
    // null, or undefined) — i.e. the default Today dashboard's own community drill-down,
    // not an override of an already-selected tab (each tab section above branches on the
    // selected community internally via `openItem`/`dashboardCommunity`).
    if (params.community_sfid) {
        return <CommunitySection />;
    }

    if (openTab && NOT_PORTED_LABELS[openTab]) {
        return <NotPortedTab label={NOT_PORTED_LABELS[openTab]} />;
    }

    // openTab === 'today', or null/undefined (Sidebar's default before any tab is clicked).
    return <TodayDashboard />;
};

export default DashboardPage;
